import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { postApplication } from '../../API/Applications/applicationService';
import { fetchAllJobs } from '../../API/recruitment/recruitmentService';
import icon from '../../images/icons/Design sans titre (44).png';
import { showDate } from '../../lib/dateFormater';
import WaterLoader from '../Loader/WaterLoader';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardFooter, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

/** Convertit du HTML en texte brut */
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const DESCRIPTION_PREVIEW_LENGTH = 200;

interface Job {
  id: number;
  title: string;
  description: string;
  createdAt: string;
  reference: string;
}

interface DataState {
  totalPages: number;
  totalCount: number;
  jobs: Job[];
}

const HiringHeroSection = () => {
  const formSchema = z.object({
    reference: z.string().optional(),
    lastName: z
      .string({ required_error: 'Ce champs est obligatoire' })
      .min(1, { message: 'Ce champs est obligatoire' }),
    firstName: z
      .string({ required_error: 'Ce champs est obligatoire' })
      .min(1, { message: 'Ce champs est obligatoire' }),
    email: z
      .string({ required_error: 'Ce champs est obligatoire' })
      .email({ message: 'Ce champs est obligatoire' }),
    phone: z
      .string({ required_error: 'Ce champs est obligatoire' })
      .min(1, { message: 'Ce champs est obligatoire' }),
    coverLetter: z.object({
      key: z.string(),
      fileName: z.string(),
      name: z.string(),
      fileSize: z.number(),
      size: z.number(),
      file: z.instanceof(File, { message: 'Ce champs est obligatoire' }),
    }),
    resume: z.object({
      key: z.string(),
      fileName: z.string(),
      name: z.string(),
      fileSize: z.number(),
      size: z.number(),
      file: z.instanceof(File, { message: 'Ce champs est obligatoire' }),
    }),
  });

  type FormData = {
    reference: string;
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
    coverLetter: { [key: string]: any } | null;
    resume: { [key: string]: any } | null;
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema), // Replace custom resolver
  });

  const { setValue, handleSubmit, reset } = form;

  const formRef = useRef<HTMLDivElement | null>(null);
  const coverLetterInputRef = useRef<HTMLInputElement | null>(null);
  const resumeInputRef = useRef<HTMLInputElement | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [data, setData] = useState<DataState>({
    totalPages: 0,
    totalCount: 0,
    jobs: [],
  });

  const fetchData = async () => {
    setLoading(true);
    const response = await fetchAllJobs(currentPage);
    if (response) {
      const { data, totalPages, totalCount } = response;
      setData({
        ...data,
        jobs: data,
        totalPages,
        totalCount,
      });
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const handlePostuler = (reference: string, event: React.MouseEvent) => {
    event.preventDefault();
    setValue('reference', reference);

    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }, 100);
  };

  const onSubmit = async (data: any) => {
    setSubmitting(true);
    const success = await postApplication(data);
    setSubmitting(false);
    if (success) {
      reset();
      // Vider les inputs file natifs (non gérés par react-hook-form)
      if (coverLetterInputRef.current) coverLetterInputRef.current.value = '';
      if (resumeInputRef.current) resumeInputRef.current.value = '';
    }
  };

  return (
    <div>
      {/* Modale description complète */}
      <Dialog open={!!selectedJob} onOpenChange={open => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-primary text-lg leading-snug">
              {selectedJob?.title}
            </DialogTitle>
            <p className="text-xs text-neutral-400">
              Publié le : {selectedJob ? showDate(selectedJob.createdAt) : ''}
            </p>
          </DialogHeader>
          <div
            className="prose prose-sm max-w-none mt-2 text-neutral-700"
            dangerouslySetInnerHTML={{ __html: selectedJob?.description ?? '' }}
          />
        </DialogContent>
      </Dialog>

      <div className="container mx-auto mt-20 mb-20 md:px-72">
        <h1 className="text-2xl font-bold text-black">Nos Offres d'emploi</h1>

        <div>
          {loading ? (
            <div className="flex justify-center">
              <WaterLoader />
            </div>
          ) : data.totalCount === 0 ? (
            <div className="flex mt-2 space-x-2">
              <div>
                <img src={icon} alt="" />
              </div>
              <div>
                Nous n'avons pas d'offres d'emploi actuellement, mais n'hésitez pas à revenir plus
                tard ou à nous suivre pour être informé des prochaines opportunités
              </div>
            </div>
          ) : (
            <div className="mt-10">
              {data.jobs.map(item => {
                const plainText = stripHtml(item?.description ?? '');
                const isLong = plainText.length > DESCRIPTION_PREVIEW_LENGTH;
                const preview = isLong
                  ? plainText.slice(0, DESCRIPTION_PREVIEW_LENGTH) + '...'
                  : plainText;

                return (
                  <Card className="p-5 mt-5 border-2 border-primary" key={item?.id}>
                    <CardTitle>
                      <div className="flex justify-end">
                        <Badge
                          className="text-end cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setSelectedJob(item)}
                        >
                          {item?.reference}
                        </Badge>
                      </div>

                      <div className="flex justify-start ml-5 ">
                        <div className="flex flex-col items-start justify-center ">
                          <div>
                            <h1
                              className="text-start cursor-pointer hover:text-primary hover:underline transition-colors"
                              onClick={() => setSelectedJob(item)}
                            >
                              {item?.title}
                            </h1>
                          </div>
                          <h1 className="text-xs text-start text-neutral-400">
                            Publié le : {showDate(item?.createdAt)}
                          </h1>
                        </div>
                      </div>
                    </CardTitle>
                    <CardContent>
                      <p className="mt-3 text-neutral-600">{preview}</p>
                      {isLong && (
                        <button
                          type="button"
                          className="mt-1 text-sm font-medium text-primary hover:underline"
                          onClick={() => setSelectedJob(item)}
                        >
                          Voir plus
                        </button>
                      )}
                    </CardContent>
                    <CardFooter>
                      <Button type="button" onClick={e => handlePostuler(item?.reference, e)}>
                        Postuler
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {!loading && data.totalPages > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={`px-4 py-2 rounded ${
                currentPage === 1 ? 'bg-gray-300' : 'bg-primary text-white hover:bg-primary-dark'
              }`}
            >
              Précédent
            </button>
            <span className="px-4 py-2 border rounded">
              {currentPage} / {data.totalPages}
            </span>
            <button
              disabled={currentPage === data.totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, data.totalPages))}
              className={`px-4 py-2 rounded ${
                currentPage === data.totalPages
                  ? 'bg-gray-300'
                  : 'bg-primary text-white hover:bg-primary-dark'
              }`}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
      <div className="">
        {' '}
        <div className="bg-primary pb-32">
          <h1 className="p-5 text-3xl text-center text-white">
            Soumettez votre candidature en ligne pour un stage, une alternance ou une demande
            d'emploi.
          </h1>
        </div>
      </div>

      <div className="relative pb-1 mt-10 mb-10 lg:mb-40 lg:mt-96 " ref={formRef}>
        <div className="left-0 right-0 w-full ml-auto mr-auto none lg:absolute top-[-430px]">
          <div className="flex justify-center ">
            <Card>
              <CardContent className="p-0 pt-0">
                <div className="relative grid gap-5 p-5 grid-rows md:grid-cols">
                  {submitting && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 rounded-lg backdrop-blur-sm">
                      <WaterLoader />
                      <p className="mt-2 text-sm font-medium text-primary">Envoi en cours...</p>
                    </div>
                  )}
                  <Form {...form}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {form.watch('reference') && (
                        <FormField
                          name="reference"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Réference</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Réference"
                                  readOnly={true}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <div className="flex flex-col lg:space-x-4 lg:flex-row">
                        <FormField
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Nom</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Nom"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Prénom</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Prénom"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <FormField
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Email</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary "
                                  placeholder="Email"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <div className="flex flex-col justify-between mt-1 lg:space-x-4 lg:flex-row">
                          <FormField
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <Label>Téléphone</Label>

                                <FormControl>
                                  <Input
                                    className="mt-5 border border-primary md:w-[440px]"
                                    placeholder="Téléphone"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col mt-2 lg:space-x-4 lg:flex-row">
                        <FormField
                          name="coverLetter"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Label>Lettre de motivation *</Label>
                              <FormControl>
                                <Input
                                  name="coverLetter"
                                  type="file"
                                  accept="application/pdf"
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Nom"
                                  ref={coverLetterInputRef}
                                  onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const mappedFile = {
                                        key: file.name,
                                        fileName: file.name,
                                        name: file.name,
                                        fileSize: file.size,
                                        size: file.size,
                                        file: file,
                                      };
                                      form.setValue('coverLetter', mappedFile, {
                                        shouldValidate: true,
                                      });
                                    }
                                  }}
                                />
                              </FormControl>

                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="resume"
                          render={({ field, fieldState }) => {
                            return (
                              <FormItem>
                                <Label>CV *</Label>
                                <FormControl>
                                  <Input
                                    type="file"
                                    accept="application/pdf"
                                    className="mt-5 border border-primary md:w-[440px]"
                                    placeholder="Prénom"
                                    ref={resumeInputRef}
                                    onChange={e => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const mappedFile = {
                                          key: file.name,
                                          fileName: file.name,
                                          name: file.name,
                                          fileSize: file.size,
                                          size: file.size,
                                          file: file,
                                        };
                                        form.setValue('resume', mappedFile, {
                                          shouldValidate: true,
                                        });
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <div className="flex mt-5 space-x-4">
                        <div>
                          <Button
                            className="w-full rounded-none lg:w-56 disabled:opacity-60 disabled:cursor-not-allowed"
                            type="submit"
                            disabled={submitting}
                          >
                            {submitting ? (
                              <span className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 animate-spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8H4z"
                                  />
                                </svg>
                                Envoi en cours...
                              </span>
                            ) : (
                              'Envoyer'
                            )}
                          </Button>
                        </div>
                        <div>
                          <Button
                            className="w-full rounded-none lg:w-56 disabled:opacity-60 disabled:cursor-not-allowed"
                            type="button"
                            disabled={submitting}
                            onClick={() => setValue('reference', '')}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringHeroSection;
